"use client";

/**
 * QueryDataDisplay - Analytics query result display
 *
 * Shows query parameters, results table, and expandable raw data.
 * Adapted from existing QueryAnalyticsDisplay for new AggregatedToolCall type.
 */

import { useState } from "react";
import { Tag, Alert, Table, TableBody, TableCell, TableHead, TableRow, TableHeader } from "@/ui";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import {
  TableChart,
  Code,
  ExpandMore,
  ExpandLess,
  SearchOff,
  FilterList,
  ViewColumn,
  Functions,
  FileDownload,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface QueryDataInput {
  explanation?: string;
  measures?: string[];
  dimensions?: string[];
  filters?: Array<{
    member: string;
    operator: string;
    values?: string[];
  }>;
}

interface QueryDataOutput {
  success?: boolean;
  status?: "success" | "error";
  data?: Record<string, unknown>[];
  rowCount?: number;
  query?: Record<string, unknown>;
  error?: string;
  suggestion?: string;
}

interface StreamingOutput {
  phase?: string;
  progress?: number;
  message?: string;
}

/** Strip "CubeName." prefix and convert camelCase to readable labels */
function humanizeMember(member: string): string {
  // Remove the "CubeName." prefix (e.g. "FloatInteraction.count" → "count")
  const field = member.includes(".") ? member.split(".").pop()! : member;
  // camelCase → spaced words, then Title Case
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

/** Format a column header from cube query results */
function humanizeColumn(col: string): string {
  // Columns come as "CubeName.fieldName" — show just the field, humanized
  const field = col.includes(".") ? col.split(".").pop()! : col;
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

/** Build display labels for a list of members, disambiguating duplicate field names
 *  e.g. ["Candidate.name", "Skill.name"] → { "Candidate.name": "Candidate Name", "Skill.name": "Skill Name" }
 *  but  ["Candidate.name", "Candidate.uuid"] → { "Candidate.name": "Name", "Candidate.uuid": "Uuid" }
 */
function buildDisplayLabels(members: string[], humanizer: (m: string) => string = humanizeMember): Record<string, string> {
  const labels: Record<string, string> = {};

  // Deduplicate members (same field in measures + dimensions shouldn't cause collision)
  const unique = [...new Set(members)];
  const humanized = unique.map(humanizer);

  // Find which humanized names map to different cubes (true collisions)
  const nameToDistinctCubes: Record<string, Set<string>> = {};
  for (let i = 0; i < unique.length; i++) {
    const h = humanized[i];
    const cube = unique[i].includes(".") ? unique[i].split(".")[0] : "";
    if (!nameToDistinctCubes[h]) nameToDistinctCubes[h] = new Set();
    nameToDistinctCubes[h].add(cube);
  }

  for (let i = 0; i < unique.length; i++) {
    const member = unique[i];
    const h = humanized[i];
    if (nameToDistinctCubes[h].size > 1 && member.includes(".")) {
      // Different cubes share same field name — include cube prefix
      const cube = member.split(".")[0];
      labels[member] = `${cube} ${h}`;
    } else {
      labels[member] = h;
    }
  }

  return labels;
}

const codeBlockStyle = {
  padding: spacing[4],
  backgroundColor: primitives.neutral[900],
  color: primitives.neutral[200],
  borderRadius: radius.md,
  overflow: "auto" as const,
  maxHeight: 300,
};

const codePreStyle = {
  margin: 0,
  fontSize: `${typography.scale.xs[0]}px`,
  fontFamily: typography.fontFamily.mono,
};

const toggleButtonStyle = {
  display: "flex" as const,
  alignItems: "center" as const,
  gap: spacing[1],
  color: colors.action.product,
  background: "none",
  border: "none",
  cursor: "pointer" as const,
  padding: spacing[1],
  borderRadius: radius.sm,
};

/** Export table data as CSV download */
function exportToCsv(
  data: Record<string, unknown>[],
  columns: string[],
  labels: Record<string, string>
) {
  const header = columns.map((c) => labels[c] || humanizeColumn(c));
  const rows = data.map((row) =>
    columns.map((col) => {
      const val = row[col];
      const str = val == null ? "" : String(val);
      // Escape quotes and wrap in quotes if contains comma/quote/newline
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    })
  );
  const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `sola-query-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function QueryDataDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showQuery, setShowQuery] = useState(false);
  const [showRawData, setShowRawData] = useState(false);

  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isComplete, isError, error, progress } = toolState;

  const input = toolCall.input as QueryDataInput | undefined;
  const result = toolCall.finalOutput as QueryDataOutput | undefined;

  // Get streaming phase message from preliminary outputs
  const lastOutput = toolCall.outputs[toolCall.outputs.length - 1];
  const streamingData = lastOutput?.preliminary
    ? (lastOutput.output as StreamingOutput)
    : null;
  const progressLabel =
    streamingData?.message || "Executing query...";

  // Check for success via either `success: true` or `status: "success"`
  const isSuccess = result?.success === true || result?.status === "success";
  const explanation = input?.explanation || "Querying analytics data...";
  const measures = input?.measures || [];
  const dimensions = input?.dimensions || [];
  const filters = input?.filters || [];
  const tableData = result?.data || [];
  const rowCount = result?.rowCount || tableData.length;
  const columns = tableData.length > 0 ? Object.keys(tableData[0]) : [];

  // Build disambiguated labels for tags and column headers
  const allMembers = [...measures, ...dimensions];
  const memberLabels = buildDisplayLabels(allMembers);
  const columnLabels = buildDisplayLabels(columns, humanizeColumn);

  const formatFilters = () => {
    if (filters.length === 0) return "None";
    return filters
      .map((f) => `${humanizeMember(f.member)} ${f.operator} ${f.values?.join(", ")}`)
      .join(", ");
  };

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={
          isLoading
            ? "Querying Analytics..."
            : isError
            ? "Query Failed"
            : "Query Complete"
        }
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<TableChart style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {/* Loading state */}
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label={progressLabel} />
          </div>
        )}

        {/* Error from execution */}
        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {/* Results */}
        {!isLoading && (
          <>
            {/* Explanation */}
            <div
              style={{
                padding: `${spacing[3]}px ${spacing[5]}px`,
                borderBottom: `1px solid ${colors.border.subtle}`,
                backgroundColor: primitives.neutral[50],
              }}
            >
              <span style={{ ...textStyles.body2, color: colors.text.primary }}>
                {explanation}
              </span>
            </div>

            {/* Query Params: Measures / Dimensions / Filters */}
            {(measures.length > 0 || dimensions.length > 0 || filters.length > 0) && (
              <div
                style={{
                  padding: `${spacing[3]}px ${spacing[5]}px`,
                  display: "flex",
                  gap: spacing[5],
                  flexWrap: "wrap",
                  borderBottom: `1px solid ${colors.border.subtle}`,
                }}
              >
                {measures.length > 0 && (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginBottom: spacing[1] }}>
                      <Functions style={{ fontSize: 14, color: colors.text.disabled }} />
                      <span style={{ ...textStyles.label, color: colors.text.muted }}>
                        Measures
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                      {measures.map((m, i) => (
                        <Tag key={i} size="sm" variant="filled" color="primary">
                          {memberLabels[m] || humanizeMember(m)}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {dimensions.length > 0 && (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginBottom: spacing[1] }}>
                      <ViewColumn style={{ fontSize: 14, color: colors.text.disabled }} />
                      <span style={{ ...textStyles.label, color: colors.text.muted }}>
                        Dimensions
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                      {dimensions.map((d, i) => (
                        <Tag key={i} size="sm" variant="default" color="primary">
                          {memberLabels[d] || humanizeMember(d)}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {filters.length > 0 && (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginBottom: spacing[1] }}>
                      <FilterList style={{ fontSize: 14, color: colors.text.disabled }} />
                      <span style={{ ...textStyles.label, color: colors.text.muted }}>
                        Filters
                      </span>
                    </div>
                    <span style={{ ...textStyles.caption, color: colors.text.primary }}>
                      {formatFilters()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Results Table */}
            {isSuccess && tableData.length > 0 && (
              <div
                style={{
                  padding: spacing[4],
                  borderTop: `1px solid ${colors.border.subtle}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing[2],
                    marginBottom: spacing[3],
                  }}
                >
                  <TableChart style={{ fontSize: 20, color: colors.action.product }} />
                  <span
                    style={{
                      ...textStyles.body2,
                      fontWeight: 600,
                      flex: 1,
                    }}
                  >
                    Results ({rowCount} {rowCount === 1 ? "row" : "rows"})
                  </span>
                  <button
                    onClick={() => exportToCsv(tableData, columns, columnLabels)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: spacing[1],
                      padding: `${spacing[1]}px ${spacing[2]}px`,
                      border: `1px solid ${colors.border.subtle}`,
                      borderRadius: radius.sm,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      ...textStyles.caption,
                      fontWeight: 500,
                      color: colors.text.muted,
                    }}
                  >
                    <FileDownload style={{ fontSize: 14 }} />
                    CSV
                  </button>
                </div>

                <div
                  style={{
                    maxHeight: 300,
                    overflow: "auto",
                    borderRadius: radius.md,
                    border: `1px solid ${colors.border.subtle}`,
                  }}
                >
                  <Table compact hoverable>
                    <TableHead>
                      <TableRow>
                        {columns.map((col) => (
                          <TableHeader
                            key={col}
                            style={{
                              ...textStyles.caption,
                              fontWeight: 600,
                              backgroundColor: colors.action.productLight,
                              color: colors.action.product,
                              borderBottom: `1px solid ${colors.border.subtle}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {columnLabels[col] || humanizeColumn(col)}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, i) => (
                        <TableRow key={i}>
                          {columns.map((col) => (
                            <TableCell
                              key={col}
                              style={{
                                ...textStyles.caption,
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {typeof row[col] === "number"
                                ? (row[col] as number).toLocaleString()
                                : String(row[col] || "-")}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Query Error */}
            {result && !isSuccess && (
              <div style={{ padding: spacing[4] }}>
                <Alert variant="error" title="Query Failed">
                  <span style={{ ...textStyles.body2 }}>
                    {result.error || "An unknown error occurred"}
                  </span>
                  {result.suggestion && (
                    <span
                      style={{
                        ...textStyles.body2,
                        marginTop: spacing[2],
                        fontStyle: "italic",
                        display: "block",
                      }}
                    >
                      {result.suggestion}
                    </span>
                  )}
                </Alert>
              </div>
            )}

            {/* Empty Results */}
            {isSuccess && tableData.length === 0 && (
              <ToolEmptyState icon={<SearchOff />} message="No results found for this query" />
            )}

            {/* Expandable Query/Raw Data */}
            <div
              style={{
                padding: `${spacing[3]}px ${spacing[4]}px`,
                borderTop: `1px solid ${colors.border.subtle}`,
                backgroundColor: primitives.neutral[50],
                borderRadius: `0 0 ${radius.lg}px ${radius.lg}px`,
              }}
            >
              <div style={{ display: "flex", gap: spacing[2] }}>
                <button onClick={() => setShowQuery(!showQuery)} style={toggleButtonStyle}>
                  <Code style={{ fontSize: 16 }} />
                  <span style={{ ...textStyles.caption, fontWeight: 600 }}>View Query</span>
                  {showQuery ? <ExpandLess style={{ fontSize: 16 }} /> : <ExpandMore style={{ fontSize: 16 }} />}
                </button>

                {isSuccess && (
                  <button onClick={() => setShowRawData(!showRawData)} style={toggleButtonStyle}>
                    <Code style={{ fontSize: 16 }} />
                    <span style={{ ...textStyles.caption, fontWeight: 600 }}>View Raw JSON</span>
                    {showRawData ? <ExpandLess style={{ fontSize: 16 }} /> : <ExpandMore style={{ fontSize: 16 }} />}
                  </button>
                )}
              </div>

              {showQuery && (
                <div style={{ marginTop: spacing[4] }}>
                  <div style={codeBlockStyle}>
                    <pre style={codePreStyle}>
                      {JSON.stringify(result?.query || input, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {showRawData && (
                <div style={{ marginTop: spacing[4] }}>
                  <div style={codeBlockStyle}>
                    <pre style={codePreStyle}>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default QueryDataDisplay;
